/*
 * Julvärmen shell: header (sticky, shrink on scroll, dropdowns, cart counts), menu drawer, search overlay
 * with predictive search, market picker, footer accordions and cookie-settings links.
 * Loaded with defer by sections/jv-header and sections/jv-footer; the guard keeps it to one run per page.
 * Behaviour lives in custom elements, so it survives Section Rendering and the theme editor.
 * Talks to the core through window.JV (assets/jv-theme.js): JV.openDialog / closeDialog, JV.cart, jv:* events.
 */
(() => {
  'use strict';

  if (window.__jvShellLoaded) return;
  window.__jvShellLoaded = true;

  const JV = (window.JV = window.JV || {});
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqPhone = window.matchMedia('(max-width: 749px)');
  const mqDesktop = window.matchMedia('(min-width: 990px)');
  const mqHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* ------------------------------------------------------------------ helpers */
  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };

  // One per connection of a component: every listener added with on() goes away with abort(), so moving a section
  // in the theme editor (disconnect, then connect again with the same nodes) never doubles them.
  const connection = () => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const added = [];
    return {
      on(target, type, handler, options = {}) {
        if (!target || typeof target.addEventListener !== 'function') return;
        const settings = typeof options === 'boolean' ? { capture: options } : { ...options };
        if (controller) settings.signal = controller.signal;
        target.addEventListener(type, handler, settings);
        added.push([target, type, handler, Boolean(settings.capture)]);
      },
      abort() {
        if (controller) controller.abort();
        added.splice(0).forEach(([target, type, handler, capture]) => target.removeEventListener(type, handler, capture));
      },
    };
  };

  const openDialog = (dialog, opener) => {
    if (!dialog) return;
    if (typeof JV.openDialog === 'function') JV.openDialog(dialog, opener);
    else if (!dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
  };

  const closeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof JV.closeDialog === 'function') JV.closeDialog(dialog);
    else if (dialog.open) dialog.close();
  };

  const plainClick = (event) =>
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  // Pluralised labels arrive from Liquid as the rendered "one" form and the "other" form for 7.
  const countLabel = (count, one, other) => {
    if (count === 1) return one || '';
    return (other || '').replace('7', String(count));
  };

  /* ------------------------------------------------------------------ dialog openers */
  // [data-jv-open="DialogId"] opens that dialog. jv-theme.js handles these as well; when it has already opened the
  // dialog it calls preventDefault and this does nothing. Plain left clicks only, so links still open in a new tab.
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || !plainClick(event)) return;
    const opener = event.target.closest && event.target.closest('[data-jv-open]');
    if (!opener) return;
    const dialog = document.getElementById(opener.getAttribute('data-jv-open'));
    if (!(dialog instanceof HTMLDialogElement)) return;
    event.preventDefault();
    openDialog(dialog, opener);
  });

  // Keep aria-expanded on every opener in step with its dialog.
  const syncOpeners = (dialog, open) => {
    if (!dialog || !dialog.id) return;
    document.querySelectorAll(`[data-jv-open="${CSS.escape(dialog.id)}"][aria-expanded]`).forEach((el) => {
      el.setAttribute('aria-expanded', String(open));
    });
  };
  document.addEventListener('jv:dialog:open', (event) => {
    if (event.target instanceof HTMLDialogElement) syncOpeners(event.target, true);
  });
  document.addEventListener('jv:dialog:close', (event) => {
    if (event.target instanceof HTMLDialogElement) syncOpeners(event.target, false);
  });

  /* ------------------------------------------------------------------ cart counts in the header */
  const updateHeaderCounts = (count, bump) => {
    const value = Math.max(0, Number(count) || 0);
    const short = value > 99 ? '99+' : String(value);
    document.querySelectorAll('[data-jv-header-count]').forEach((el) => {
      el.textContent = short;
      if (el.classList.contains('jv-header__badge')) {
        el.hidden = value < 1 && !el.hasAttribute('data-show-empty');
        if (bump && !reducedMotion.matches) {
          el.classList.remove('is-bumped');
          void el.offsetWidth;
          el.classList.add('is-bumped');
        }
      }
    });
    document.querySelectorAll('[data-jv-header-cart]').forEach((link) => {
      const label = countLabel(value, link.dataset.labelOne, link.dataset.labelOther);
      if (label) link.setAttribute('aria-label', label);
    });
  };
  JV.updateHeaderCounts = updateHeaderCounts;

  document.addEventListener('jv:cart:change', (event) => {
    const detail = event.detail || {};
    if (detail.cart && typeof detail.cart.item_count === 'number') {
      updateHeaderCounts(detail.cart.item_count, detail.action === 'add');
    }
  });
  // Horizon blocks the merchant may add publish their own cart event.
  document.addEventListener('cart:update', (event) => {
    const detail = event.detail || {};
    const resource = detail.resource || {};
    const data = detail.data || {};
    const count = typeof resource.item_count === 'number' ? resource.item_count : data.itemCount;
    if (typeof count === 'number') updateHeaderCounts(count, true);
  });
  // Back/forward cache: the page comes back as it was, the cart may not have.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || !JV.cart || typeof JV.cart.get !== 'function') return;
    JV.cart
      .get()
      .then((cart) => updateHeaderCounts(cart.item_count, false))
      .catch(() => {});
  });

  /* ------------------------------------------------------------------ header */
  class JvHeader extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.jv-header');
      if (!this.root) return;
      this.sticky = this.root.dataset.sticky === 'true';
      this.shrink = this.root.dataset.shrink === 'true';
      this.classList.add('is-ready');
      if (this.connection) this.connection.abort();
      this.connection = connection();

      this.setupSticky();
      this.setupMetrics();
      this.setupNav();

      this.onScroll = this.onScroll.bind(this);
      this.scroller = document.querySelector('.page-wrapper');
      const { on } = this.connection;
      on(window, 'scroll', this.onScroll, { passive: true });
      if (this.scroller) on(this.scroller, 'scroll', this.onScroll, { passive: true });
      on(mqDesktop, 'change', this.onScroll);
      this.onScroll();
    }

    disconnectedCallback() {
      if (this.connection) this.connection.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      (this.items || []).forEach((item) => window.clearTimeout(item._jvTimer));
      // The header is gone (removed in the editor): nothing is stuck to the top any more.
      document.documentElement.style.setProperty('--jv-header-height', '0px');
    }

    // Browsers without :has(): the class versions of the sticky rules in the section stylesheet.
    setupSticky() {
      const supportsHas = Boolean(window.CSS && CSS.supports && CSS.supports('selector(:has(*))'));
      if (supportsHas) return;
      const wrapper = this.root.closest('.shopify-section');
      const group = document.getElementById('header-group');
      if (wrapper) wrapper.classList.toggle('jv-is-sticky', this.sticky);
      if (group) group.classList.toggle('jv-header-group--contents', this.sticky);
    }

    // --jv-header-height on :root: the height that stays stuck while scrolling (0 when sticky is off), used for
    // anchor offsets and sticky elements below the header. Horizon's --header-height / --header-group-height on body
    // keep Horizon sections the merchant adds in step.
    setupMetrics() {
      const update = () => {
        if (!this.root.isConnected) return;
        const height = Math.round(this.root.getBoundingClientRect().height);
        const group = document.getElementById('header-group');
        let groupHeight = 0;
        if (group) {
          [...group.children].forEach((child) => {
            groupHeight += child.getBoundingClientRect().height;
          });
        }
        const style = document.body.style;
        style.setProperty('--header-height', `${height}px`);
        style.setProperty('--header-group-height', `${Math.round(groupHeight || height)}px`);
        if (!style.getPropertyValue('--transparent-header-offset-boolean')) {
          style.setProperty('--transparent-header-offset-boolean', '0');
        }
        document.documentElement.style.setProperty('--jv-header-height', this.sticky ? `${height}px` : '0px');
      };
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(update);
        this.resizeObserver.observe(this.root);
        const group = document.getElementById('header-group');
        if (group) [...group.children].forEach((child) => this.resizeObserver.observe(child));
      }
      update();
    }

    // Desktop: a little lower once the visitor has scrolled. Two thresholds, so the change in height can never make
    // the header flick back and forth.
    onScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.scrollTicking = false;
        if (!this.root) return;
        const y = Math.max(window.scrollY || 0, this.scroller ? this.scroller.scrollTop : 0);
        this.root.classList.toggle('is-scrolled', y > 4);
        if (!this.sticky || !this.shrink || !mqDesktop.matches) {
          this.root.classList.remove('is-shrunk');
          return;
        }
        const shrunk = this.root.classList.contains('is-shrunk');
        if (!shrunk && y > 160) this.root.classList.add('is-shrunk');
        else if (shrunk && y < 40) this.root.classList.remove('is-shrunk');
      });
    }

    setupNav() {
      const { on } = this.connection;
      this.items = [...this.querySelectorAll('.jv-header__item--parent')];
      this.items.forEach((item) => {
        const toggle = item.querySelector('[data-jv-nav-toggle]');
        if (!toggle) return;
        on(toggle, 'click', (event) => {
          event.preventDefault();
          window.clearTimeout(item._jvTimer);
          // The pointer already opened this dropdown: clicking its toggle keeps it open (a second click closes it).
          if (item._jvHoverOpen && item.classList.contains('is-open')) {
            item._jvHoverOpen = false;
            return;
          }
          this.toggleItem(item, !item.classList.contains('is-open'));
        });
        on(item, 'mouseenter', () => {
          if (!mqHover.matches) return;
          window.clearTimeout(item._jvTimer);
          item._jvTimer = window.setTimeout(() => {
            if (item.classList.contains('is-open')) return;
            this.toggleItem(item, true);
            item._jvHoverOpen = true;
          }, 70);
        });
        on(item, 'mouseleave', () => {
          if (!mqHover.matches) return;
          window.clearTimeout(item._jvTimer);
          item._jvTimer = window.setTimeout(() => this.toggleItem(item, false), 200);
        });
        on(item, 'focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.toggleItem(item, false);
        });
        on(item, 'keydown', (event) => {
          if (event.key !== 'Escape' || !item.classList.contains('is-open')) return;
          event.preventDefault();
          event.stopPropagation();
          this.toggleItem(item, false);
          toggle.focus();
        });
      });
      on(document, 'click', (event) => {
        if (!this.contains(event.target)) this.closeAll();
      });
    }

    closeAll(except) {
      (this.items || []).forEach((item) => {
        if (item !== except && item.classList.contains('is-open')) this.toggleItem(item, false);
      });
    }

    toggleItem(item, open) {
      if (open) {
        this.closeAll(item);
        this.positionPanel(item);
      } else {
        item._jvHoverOpen = false;
      }
      item.classList.toggle('is-open', open);
      const toggle = item.querySelector('[data-jv-nav-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
    }

    // Keeps a dropdown inside the viewport when its menu item sits near the edge.
    positionPanel(item) {
      const panel = item.querySelector('.jv-header__panel');
      if (!panel) return;
      panel.style.setProperty('--jv-panel-shift', '0px');
      const rect = panel.getBoundingClientRect();
      const margin = 16;
      let shift = 0;
      if (rect.left < margin) shift = margin - rect.left;
      else if (rect.right > window.innerWidth - margin) shift = window.innerWidth - margin - rect.right;
      if (shift) panel.style.setProperty('--jv-panel-shift', `${Math.round(shift)}px`);
    }

    // The cart link carries data-jv-cart-open: assets/jv-cart.js (cart drawer section) opens the drawer from it,
    // and without the drawer, or with "Cart type: page", it is a plain link to the cart page.
  }
  define('jv-header', JvHeader);

  /* ------------------------------------------------------------------ menu drawer */
  class JvMenuDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.closest('dialog');
      this.rootPane = this.querySelector('.jv-drawer__pane');
      this.scrollArea = this.querySelector('.jv-drawer__scroll');
      this.stack = [];

      if (this.connection) this.connection.abort();
      this.connection = connection();
      const { on } = this.connection;
      on(this, 'click', (event) => {
        const opener = event.target.closest('[data-jv-pane-open]');
        if (opener && this.contains(opener)) {
          event.preventDefault();
          this.open(opener);
          return;
        }
        const back = event.target.closest('[data-jv-pane-back]');
        if (back && this.contains(back)) {
          event.preventDefault();
          this.back();
        }
      });
      on(this, 'keydown', (event) => {
        // Escape inside a sub-menu steps back first; at the top it closes the drawer as usual.
        if (event.key !== 'Escape' || !this.stack.length) return;
        if (event.target.closest && event.target.closest('jv-market details[open]')) return;
        event.preventDefault();
        event.stopPropagation();
        this.back();
      });
      on(this.dialog, 'jv:dialog:close', () => this.reset());
      // Grown to desktop width while open: the drawer has nothing to offer there.
      on(mqDesktop, 'change', (event) => {
        if (event.matches && this.dialog && this.dialog.open) closeDialog(this.dialog);
      });
    }

    disconnectedCallback() {
      if (this.connection) this.connection.abort();
    }

    current() {
      return this.stack.length ? this.stack[this.stack.length - 1].pane : this.rootPane;
    }

    open(opener) {
      const pane = this.querySelector(`#${CSS.escape(opener.dataset.jvPaneOpen)}`);
      if (!pane) return;
      const from = this.current();
      this.stack.push({ pane, opener });
      opener.setAttribute('aria-expanded', 'true');
      from.classList.remove('is-active');
      from.classList.add('is-behind');
      from.inert = true;
      pane.inert = false;
      pane.classList.remove('is-behind');
      pane.classList.add('is-active');
      if (this.scrollArea) this.scrollArea.scrollTop = 0;
      this.focusLater(pane.querySelector('[data-jv-pane-back]'));
    }

    back() {
      const entry = this.stack.pop();
      if (!entry) return;
      const to = this.current();
      entry.opener.setAttribute('aria-expanded', 'false');
      entry.pane.classList.remove('is-active');
      entry.pane.inert = true;
      to.classList.remove('is-behind');
      to.classList.add('is-active');
      to.inert = false;
      this.focusLater(entry.opener);
    }

    reset() {
      while (this.stack.length) {
        const entry = this.stack.pop();
        entry.opener.setAttribute('aria-expanded', 'false');
        entry.pane.classList.remove('is-active', 'is-behind');
        entry.pane.inert = true;
      }
      if (this.rootPane) {
        this.rootPane.classList.remove('is-behind');
        this.rootPane.classList.add('is-active');
        this.rootPane.inert = false;
      }
      this.querySelectorAll('jv-market').forEach((market) => market.close && market.close());
    }

    // A short wait even with reduced motion: jv-base.css shortens transitions to 0.01ms there, and a pane that is
    // still mid-way from visibility: hidden at 0ms refuses focus.
    focusLater(element) {
      if (!element) return;
      window.setTimeout(() => element.focus({ preventScroll: true }), reducedMotion.matches ? 40 : 60);
    }
  }
  define('jv-menu-drawer', JvMenuDrawer);

  /* ------------------------------------------------------------------ search overlay */
  class JvSearch extends HTMLElement {
    connectedCallback() {
      this.dialog = this.closest('dialog');
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-jv-search-input]');
      this.results = this.querySelector('[data-jv-search-results]');
      this.message = this.querySelector('[data-jv-search-message]');
      this.status = this.querySelector('[data-jv-search-status]');
      this.popular = this.querySelector('[data-jv-search-popular]');
      this.clearButton = this.querySelector('[data-jv-search-clear]');
      if (!this.input || !this.results) return;
      this.cache = new Map();
      this.activeIndex = -1;

      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onOpen = () => {
        this.input.focus({ preventScroll: true });
        this.input.select();
        requestAnimationFrame(() => {
          if (document.activeElement !== this.input) this.input.focus({ preventScroll: true });
        });
      };
      this.onClose = () => {
        this.abort();
        window.clearTimeout(this.timer);
        this.setActive(-1);
      };
      this.onClear = () => {
        this.input.value = '';
        this.onInput();
        this.input.focus();
      };
      this.onSubmit = (event) => {
        if (!this.input.value.trim()) {
          event.preventDefault();
          this.input.focus();
        }
      };

      if (this.connection) this.connection.abort();
      this.connection = connection();
      const { on } = this.connection;
      on(this.input, 'input', this.onInput);
      on(this.input, 'keydown', this.onKeydown);
      on(this.clearButton, 'click', this.onClear);
      on(this.form, 'submit', this.onSubmit);
      on(this.dialog, 'jv:dialog:open', this.onOpen);
      on(this.dialog, 'jv:dialog:close', this.onClose);
    }

    disconnectedCallback() {
      this.abort();
      window.clearTimeout(this.timer);
      if (this.connection) this.connection.abort();
    }

    abort() {
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    onInput() {
      const query = this.input.value.trim();
      if (this.clearButton) this.clearButton.hidden = !this.input.value;
      window.clearTimeout(this.timer);
      if (!query) {
        this.abort();
        this.renderIdle();
        return;
      }
      this.timer = window.setTimeout(() => this.search(query), 220);
    }

    async search(query) {
      const key = query.toLowerCase();
      if (this.cache.has(key)) {
        this.render(this.cache.get(key));
        return;
      }
      this.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.results.classList.add('is-loading');
      try {
        const url = new URL(this.dataset.url || '/search/suggest', window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection,page,query');
        url.searchParams.set('resources[limit]', '4');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('section_id', this.dataset.section || 'jv-predictive-search');
        const response = await fetch(url.toString(), { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Search failed (${response.status})`);
        const html = await response.text();
        if (this.cache.size > 30) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(key, html);
        if (this.input.value.trim().toLowerCase() === key) this.render(html);
      } catch (error) {
        if (error.name !== 'AbortError') this.results.classList.remove('is-loading');
      } finally {
        if (this.controller === controller) this.controller = null;
      }
    }

    render(html) {
      this.results.classList.remove('is-loading');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const root = doc.querySelector('[data-jv-ps]');
      const options = root && root.querySelector('[data-jv-ps-options]');
      const empty = root && root.querySelector('[data-jv-ps-empty]');
      const count = root ? Number(root.dataset.count || 0) : 0;

      this.results.replaceChildren(...(options ? [document.importNode(options, true)] : []));
      this.message.replaceChildren(...(empty ? [document.importNode(empty, true)] : []));
      const hasOptions = Boolean(this.results.querySelector('[role="option"]'));
      if (hasOptions) this.results.setAttribute('role', 'listbox');
      else this.results.removeAttribute('role');
      this.input.setAttribute('aria-expanded', String(hasOptions));
      if (this.popular) this.popular.hidden = count > 0;
      this.setActive(-1);

      let status = '';
      if (count > 0) status = countLabel(count, this.dataset.resultsOne, this.dataset.resultsOther);
      else if (empty) status = empty.textContent.replace(/\s+/g, ' ').trim();
      this.status.textContent = status;
    }

    renderIdle() {
      this.results.classList.remove('is-loading');
      this.results.replaceChildren();
      this.results.removeAttribute('role');
      this.message.replaceChildren();
      this.input.setAttribute('aria-expanded', 'false');
      if (this.popular) this.popular.hidden = false;
      this.setActive(-1);
      this.status.textContent = '';
    }

    options() {
      return [...this.results.querySelectorAll('[role="option"]')];
    }

    setActive(index) {
      const options = this.options();
      options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
      this.activeIndex = index;
      const active = options[index];
      if (active) {
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onKeydown(event) {
      const options = this.options();
      switch (event.key) {
        case 'ArrowDown':
          if (!options.length) return;
          event.preventDefault();
          this.setActive(this.activeIndex + 1 >= options.length ? 0 : this.activeIndex + 1);
          break;
        case 'ArrowUp':
          if (!options.length) return;
          event.preventDefault();
          this.setActive(this.activeIndex <= 0 ? options.length - 1 : this.activeIndex - 1);
          break;
        case 'Enter':
          if (this.activeIndex >= 0 && options[this.activeIndex]) {
            event.preventDefault();
            options[this.activeIndex].click();
          }
          break;
        case 'Escape':
          event.preventDefault();
          closeDialog(this.dialog);
          break;
        default:
          break;
      }
    }
  }
  define('jv-search', JvSearch);

  /* ------------------------------------------------------------------ market picker */
  // Native details/summary does the opening; this closes it on an outside click, on Escape, when focus leaves,
  // and when another picker opens.
  class JvMarket extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.summary = this.details.querySelector('summary');
      if (this.connection) this.connection.abort();
      this.connection = connection();
      const { on } = this.connection;
      on(document, 'click', (event) => {
        if (this.details.open && !this.contains(event.target)) this.close();
      });
      on(this, 'keydown', (event) => {
        if (event.key !== 'Escape' || !this.details.open) return;
        event.preventDefault();
        event.stopPropagation();
        this.close();
        if (this.summary) this.summary.focus();
      });
      on(this, 'focusout', (event) => {
        if (this.details.open && event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      });
      on(this.details, 'toggle', () => {
        if (!this.details.open) return;
        document.querySelectorAll('jv-market').forEach((other) => {
          if (other !== this && typeof other.close === 'function') other.close();
        });
      });
    }

    disconnectedCallback() {
      if (this.connection) this.connection.abort();
    }

    close() {
      if (this.details && this.details.open) this.details.open = false;
    }
  }
  define('jv-market', JvMarket);

  /* ------------------------------------------------------------------ footer */
  // Phones: the columns are accordions, closed to start with. Wider: always open, and the summary does nothing.
  class JvFooter extends HTMLElement {
    connectedCallback() {
      this.details = [...this.querySelectorAll('[data-jv-footer-details]')];
      if (!this.details.length) return;
      this.sync = (initial) => {
        const phone = mqPhone.matches;
        this.details.forEach((details) => {
          const summary = details.querySelector('summary');
          if (phone) {
            if (initial === true) details.open = false;
            if (summary) summary.removeAttribute('tabindex');
          } else {
            details.open = true;
            if (summary) summary.setAttribute('tabindex', '-1');
          }
        });
      };
      // Capture: runs before the smooth-details handler in jv-theme.js and keeps desktop columns open.
      this.onClick = (event) => {
        if (!mqPhone.matches && event.target.closest('summary')) {
          event.preventDefault();
          event.stopPropagation();
        }
      };
      this.onChange = () => this.sync(true);
      this.sync(true);
      mqPhone.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick, true);
    }

    disconnectedCallback() {
      if (this.onChange) mqPhone.removeEventListener('change', this.onChange);
      if (this.onClick) this.removeEventListener('click', this.onClick, true);
    }
  }
  define('jv-footer', JvFooter);

  /* ------------------------------------------------------------------ cookie settings links */
  // <a href="/pages/cookiepolicy" data-jv-open="JVCookiePrefs" data-jv-cookie-settings> (footer legal link).
  // The opener handler above opens the cookie banner's preferences dialog when it is on the page. Without it, this
  // tries JV.openCookiePreferences or Shopify's own cookie banner; otherwise the link goes to the cookie policy.
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || !plainClick(event)) return;
    const trigger = event.target.closest && event.target.closest('[data-jv-cookie-settings]');
    if (!trigger) return;
    if (typeof JV.openCookiePreferences === 'function') {
      event.preventDefault();
      JV.openCookiePreferences(trigger);
      return;
    }
    const banner = window.privacyBanner;
    if (banner && typeof banner.showPreferences === 'function') {
      event.preventDefault();
      banner.showPreferences();
    }
  });

  /* ------------------------------------------------------------------ theme editor */
  if (designMode) {
    // A re-rendered header starts with its dialogs closed: drop any stale scroll lock.
    document.addEventListener('shopify:section:load', () => {
      if (typeof JV.syncScrollLock === 'function') window.setTimeout(JV.syncScrollLock, 0);
    });
  }
})();
